import { Box, Stack, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { every, get, isArray, isEmpty, isNil, pickBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import ExportDataTable from 'src/components/ExportData';
import { EXPORT_TABLE_TYPE, URL_EXPORT_MAPPING } from 'src/components/ExportData/config';
import LineChart from 'src/components/HighChart/LineChart';
import CustomTable from 'src/components/Table';
import CompanyTradeDataWhiteBox from 'src/features/CompanyTradeData/common/CompanyTradeDataWhiteBox';
import DataGuard from 'src/features/CompanyTradeData/common/DataGuard';
import FilterChart from 'src/features/CompanyTradeData/common/FilterChart';
import PopupBuyersSuppliersList from 'src/features/CompanyTradeData/common/PopupBuyersSuppliersList';
import { TradeDataUnitByFilter } from 'src/features/TradeData/Common/TradeDataUnitbyFilter';
import useCreditFlow from 'src/hooks/useCreditFlow';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryGetTradeDataMarketAnalyticsChart } from 'src/services/queries/useQueryTradeDataWithoutHSCode';
import { FORMAT_DATE_TIME, LIMIT_GET_LIST } from 'src/utils/constants/config';
import { KeyFilterChartTradeDataUnitBy, TypeBuyerSupplier } from 'src/utils/constants/enum';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import { renderNumberOrDefault, renderValueNumberTrade } from 'src/utils/helpers/stringHelpers';
import { formatValueUSD } from 'src/utils/helpers/utilities';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';

const unitFieldMapping = {
  bol: {
    field: 'bol',
    label: 'Number of B/L',
  },
  weight: {
    field: 'weight',
    label: 'Weight',
  },
  quantity: { field: 'quantity', label: 'Quantity' },
  value_usd: { field: 'value_usd', label: 'USD' },
};

const Analytics = () => {
  const queryParams = useQueryParams();
  const tradeDataUnitByFilter = useMemo(() => new TradeDataUnitByFilter(), []);

  const [unitFilter, setUnitFilter] = useState(tradeDataUnitByFilter.value);

  const {
    palette: { text },
  } = useTheme();
  const { setSort: setTableSort, marketAnalyticsTableSortParams } = useTradeDataSortStore();

  const [marketAnalyticsTablePaging, setMarketAnalyticsTablePagingModel] = useState({
    page: 0,
    page_size: LIMIT_GET_LIST,
  });
  const [selectedRangeTime, setSelectedRangeTime] = useState({
    fromTime: queryParams.start_date,
    toTime: queryParams.end_date,
  } as {
    fromTime: string;
    toTime: string;
  });

  const { t } = useTranslation();

  const configMarketAnalyticsTableColumns: ConfigCell<IMarketAnalytics>[] = [
    {
      disablePadding: false,
      id: 'month',
      label: t('market_analytics.table.column_name.month'),
      cell: (data: IMarketAnalytics) => (
        <Typography variant="body2" color="text.secondary">
          {dayjs(data.month).format(FORMAT_DATE_TIME.yyyy_mm)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('market_analytics.table.column_name.no_of_bl'),
      cell: (data: IMarketAnalytics) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.bol)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('market_analytics.table.column_name.weight'),
      cell: (data: IMarketAnalytics) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('market_analytics.table.column_name.teu'),
      cell: (data: IMarketAnalytics) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderNumberOrDefault(data.quantity)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      cell: (data: IMarketAnalytics) => (
        <Typography variant="body2" color={'text.secondary'}>
          {formatValueUSD(data.value_usd)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'suppliers_list',
      label: 'Suppliers List',
      cell: (data: IMarketAnalytics) => (
        <Typography
          component={'span'}
          onClick={() =>
            setViewBuyersSuppliersList({
              open: true,
              type: TypeBuyerSupplier.supplier,
              start_date: dayjs(data.month, FORMAT_DATE_TIME.yyyy_mm)
                .startOf('month')
                .format(FORMAT_DATE_TIME.yyyy_mm_dd),
              end_date: dayjs(data.month, FORMAT_DATE_TIME.yyyy_mm).endOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
            })
          }
          variant="body2"
          color={'secondary.main'}
        >
          {renderValueNumberTrade(data.suppliers)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'buyers_list',
      label: 'Buyers List',
      cell: (data: IMarketAnalytics) => (
        <Typography
          component={'span'}
          onClick={() =>
            setViewBuyersSuppliersList({
              open: true,
              type: TypeBuyerSupplier.buyer,
              start_date: dayjs(data.month, FORMAT_DATE_TIME.yyyy_mm)
                .startOf('month')
                .format(FORMAT_DATE_TIME.yyyy_mm_dd),
              end_date: dayjs(data.month, FORMAT_DATE_TIME.yyyy_mm).endOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
            })
          }
          variant="body2"
          color={'secondary.main'}
        >
          {renderValueNumberTrade(data.buyers)}
        </Typography>
      ),
    },
  ];

  const marketAnalyticsTableRequestPayload: Record<string, string | number> = useMemo(() => {
    return pickBy(
      {
        ...queryParams,
        start_date: selectedRangeTime.fromTime,
        end_date: selectedRangeTime.toTime,
        offset: marketAnalyticsTablePaging.page,
        limit: marketAnalyticsTablePaging.page_size,
        sort_by: marketAnalyticsTableSortParams?.sortBy,
        order: marketAnalyticsTableSortParams?.sort,
      },
      (value) => !isNil(value) && value !== '',
    );
  }, [queryParams, marketAnalyticsTablePaging, selectedRangeTime, marketAnalyticsTableSortParams]);

  const [acceptCreditPayment, setAcceptCreditPayment] = useState(true);
  const { data, isFetching, ...rest } = useQueryGetTradeDataMarketAnalyticsChart(marketAnalyticsTableRequestPayload);

  const checkIsEmptyDataAndAutoChangeUnit = () => {
    if (isFetching || tradeDataUnitByFilter.isUserClickBack(unitFilter as KeyFilterChartTradeDataUnitBy)) return;
    const isEmptyData = isEmpty(data?.data) || every(data?.data, (item) => get(item, unitFilter) === 0);
    if (isEmptyData) {
      setUnitFilter(tradeDataUnitByFilter.next());
    }
  };

  useEffect(() => {
    checkIsEmptyDataAndAutoChangeUnit();
  }, [data, isFetching]);

  const [shouldShowBillableDataLoading, hasEnoughCredit] = useCreditFlow({
    response: data,
    featureInfo: {
      featureId: LIST_FEATURES.COMPANY_PROFILE_DATA_ANALYTICS,
      action: LIST_ACTIONS.VIEW,
    },
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
  });

  const [viewBuyersSuppliersList, setViewBuyersSuppliersList] = useState({
    open: false as boolean,
    type: TypeBuyerSupplier.supplier as TypeBuyerSupplier,
    start_date: dayjs().toNow(),
    end_date: dayjs().toNow(),
  });

  const chartFieldNameByUnitFilter = unitFieldMapping[unitFilter].field;
  const chartLabelByUnitFilter = unitFieldMapping[unitFilter].label;

  const lineChartYData = useMemo(() => {
    if (isArray(data?.data)) {
      return data?.data?.map((item: IMarketAnalytics) => [
        item?.[chartFieldNameByUnitFilter as keyof IMarketAnalytics],
      ]);
    }
    return [];
  }, [JSON.stringify(data.data), chartFieldNameByUnitFilter]);

  const lineChartXData = useMemo(() => {
    if (isArray(data?.data)) {
      return data?.data?.map((item: IMarketAnalytics) => dayjs(item.month).format(FORMAT_DATE_TIME.yyyy_mm));
    }
    return [];
  }, [JSON.stringify(data.data), chartFieldNameByUnitFilter]);

  const isDataReturnedEmpty = !shouldShowBillableDataLoading && !isFetching && isEmpty(data?.data);

  return (
    <Stack spacing={3}>
      <PopupBuyersSuppliersList
        startDate={viewBuyersSuppliersList.start_date}
        endDate={viewBuyersSuppliersList.end_date}
        payload={marketAnalyticsTableRequestPayload}
        type={viewBuyersSuppliersList.type}
        open={viewBuyersSuppliersList.open}
        handleOpenChange={(open) => setViewBuyersSuppliersList((prev) => ({ ...prev, open: open }))}
      />
      <CompanyTradeDataWhiteBox>
        <FilterChart
          selectedUnitBy={unitFilter}
          onSelectUnitBy={(unit) => {
            setUnitFilter(unit as any);
          }}
          selectedRangeTime={selectedRangeTime}
          onSelectTimeRange={({ fromTime, toTime }) => {
            setSelectedRangeTime({
              fromTime: fromTime,
              toTime: toTime,
            });
          }}
          title={'Total Bill of Lading Chart'}
        />
        <DataGuard
          shouldRenderEmpty={() => isDataReturnedEmpty}
          emptyTitle={hasEnoughCredit ? 'Trade Data Not Found' : 'Not Enough Credit'}
          emptyContent={
            hasEnoughCredit ? (
              <>
                Customs data may be restricted, incomplete, or obscured by alternative trading names or third-party
                logistics.
              </>
            ) : (
              'Can not access the data, please upgrade your plan'
            )
          }
        >
          <LineChart
            loading={shouldShowBillableDataLoading || isFetching}
            dataPropertyKeys={['y', 'custom.number_of_suppliers', 'custom.number_of_buyers']}
            tooltipFormatter={function (this) {
              return `<b>${dayjs(this.x).format(FORMAT_DATE_TIME.yyyy_mm)}</b><br>
            <span style="color: ${text.secondary}">${chartLabelByUnitFilter}: <b style="color: white">${renderNumberOrDefault(this.y)}</b></span><br>`;
            }}
            data={lineChartYData}
            categories={lineChartXData}
          />
        </DataGuard>
      </CompanyTradeDataWhiteBox>

      <CompanyTradeDataWhiteBox>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">Total Bill of Lading Over Time Table</Typography>
          <ExportDataTable
            fileName="ctd-analytics"
            featureId={LIST_FEATURES.COMPANY_PROFILE_DATA_ANALYTICS}
            endpoint={get(URL_EXPORT_MAPPING, EXPORT_TABLE_TYPE.CTD_ANALYTICS)}
            paramsExport={marketAnalyticsTableRequestPayload}
            totalRecordsAvailable={data?.total_records || 0}
          />
        </Stack>
        <CustomTable
          handleSort={(data) => {
            setTableSort('marketAnalyticsTableSortParams', data);
          }}
          onChangePage={(value) => setMarketAnalyticsTablePagingModel({ ...marketAnalyticsTablePaging, page: value })}
          onChangePageSize={(value) => setMarketAnalyticsTablePagingModel({ page: 0, page_size: value })}
          loading={shouldShowBillableDataLoading || isFetching}
          page={marketAnalyticsTablePaging.page}
          pageSize={marketAnalyticsTablePaging.page_size}
          totalRecords={data?.total_records || 0}
          dataKey={'date'}
          data={data?.data ?? []}
          configColumn={configMarketAnalyticsTableColumns}
        />
      </CompanyTradeDataWhiteBox>
    </Stack>
  );
};

export default Analytics;
